import type { ConversationAuthPair } from 'utils/conversationUserAuthHeader'

import { getConversationsHistory } from './conversationsApi'
import {
  ConversationsApiError,
  describeConversationsError,
} from './conversationsApiError'

/**
 * Fase 2 da migração: lê `GET /history` **em paralelo** ao `getMessages()` do
 * SDK, que continua sendo a fonte real, e mede a divergência entre os dois.
 * Zero mudança de UI.
 *
 * O objetivo é decidir com dado, não com opinião, se a API pode substituir o
 * SDK como fonte de leitura na Fase 3. O portão para avançar é divergência zero
 * por 7 dias em volume real.
 *
 * **Compliance**: conteúdo de chat é dado clínico. Daqui só sai contagem e, no
 * máximo, o primeiro `messageSid` divergente — nunca corpo de mensagem, autor,
 * nome de anexo ou identidade.
 */

/** Teto de páginas por execução: guarda contra cursor que não termina. */
/**
 * Teto de páginas por execução.
 *
 * Com `order=desc` a primeira página já cobre a janela que o SDK abre, então o
 * caso normal é uma requisição só. O teto existe para conversa muito longa e
 * para cursor que não termina.
 */
const MAX_PAGES = 5

export interface ShadowReadDivergence {
  /** Está no SDK e não veio da API. */
  missingInApiCount: number
  /** Veio da API e não está no SDK. */
  extraInApiCount: number
  sdkCount: number
  apiCount: number
  pagesFetched: number
  /** `true` quando a paginação parou no teto em vez de esgotar o cursor. */
  truncated: boolean
  /** Só o primeiro de cada lado, para investigação. Sid não é conteúdo. */
  firstMissingSid?: string
  firstExtraSid?: string
}

export interface ShadowReadResult {
  status: 'match' | 'divergent' | 'empty-history' | 'failed'
  divergence?: ShadowReadDivergence
  /** Preenchido em `failed`: contexto seguro do erro. */
  errorContext?: Record<string, unknown>
  /** Preenchido em `failed`: o que a telemetria consegue agrupar. */
  errorStatus?: number
  errorCode?: string
}

/**
 * Busca da mensagem mais recente para trás, parando assim que todos os sids do
 * SDK aparecerem.
 *
 * A versão anterior varria o histórico inteiro (até 50 páginas) a cada abertura
 * de chat — caro numa migração motivada por carga — e comparava histórico
 * completo contra a janela do SDK, o que fazia `extraInApiCount` ser sempre
 * positivo e tornava "divergência zero" inatingível.
 */
async function fetchHistorySidsCovering(
  sdkSids: string[],
  pair: ConversationAuthPair
): Promise<{ sids: string[]; pages: number; truncated: boolean }> {
  const restam = new Set(sdkSids)
  const sids: string[] = []
  let cursor: string | undefined
  let pages = 0

  do {
    const page = await getConversationsHistory(
      {
        personId: pair.personId,
        professionalId: pair.professionalId,
        cursor,
        limit: 100,
        order: 'desc',
      },
      pair
    )
    pages += 1
    page.items.forEach((item) => {
      if (item && typeof item.messageSid === 'string') {
        sids.push(item.messageSid)
        restam.delete(item.messageSid)
      }
    })
    cursor = page.nextCursor ?? undefined

    // Cobriu a janela do SDK: o resto é histórico antigo e não acrescenta nada
    // à pergunta que esta medição faz.
    if (restam.size === 0) return { sids, pages, truncated: false }
  } while (cursor && pages < MAX_PAGES)

  return { sids, pages, truncated: restam.size > 0 }
}

/**
 * Compara o conjunto de `messageSid` do SDK com o da API.
 *
 * `messageSid` é a única chave possível: índice reinicia por conversa e
 * timestamp repete dentro do mesmo segundo, então nenhum dos dois identifica
 * mensagem entre duas fontes.
 */
export function compareMessageSids(
  sdkSids: string[],
  apiSids: string[]
): ShadowReadDivergence {
  const inApi = new Set(apiSids)
  const inSdk = new Set(sdkSids)

  const missing = sdkSids.filter((sid) => !inApi.has(sid))
  const extra = apiSids.filter((sid) => !inSdk.has(sid))

  return {
    missingInApiCount: missing.length,
    extraInApiCount: extra.length,
    sdkCount: inSdk.size,
    apiCount: inApi.size,
    pagesFetched: 0,
    truncated: false,
    ...(missing.length > 0 ? { firstMissingSid: missing[0] } : {}),
    ...(extra.length > 0 ? { firstExtraSid: extra[0] } : {}),
  }
}

/**
 * Executa a comparação. Nunca lança: é observação em segundo plano e não pode
 * afetar o chat que o profissional está usando.
 */
export async function runHistoryShadowRead(
  sdkSids: string[],
  pair: ConversationAuthPair
): Promise<ShadowReadResult> {
  try {
    const { sids, pages, truncated } = await fetchHistorySidsCovering(
      sdkSids,
      pair
    )
    const divergence = {
      ...compareMessageSids(sdkSids, sids),
      pagesFetched: pages,
      truncated,
    }

    // Só o que falta na API reprova; sobra é histórico antigo, e esperado.
    const igual = divergence.missingInApiCount === 0

    return { status: igual ? 'match' : 'divergent', divergence }
  } catch (err) {
    // Par sem conversa devolve 404 — é estado vazio, não falha do shadow read.
    if (err instanceof ConversationsApiError && err.status === 404) {
      return { status: 'empty-history' }
    }
    return {
      status: 'failed',
      errorContext: describeConversationsError(err),
      ...(err instanceof ConversationsApiError
        ? {
            errorStatus: err.status,
            ...(err.code ? { errorCode: err.code } : {}),
          }
        : {}),
    }
  }
}
